import React from 'react';
import { Card, Typography, Box, Grid, Stack, CircularProgress, LinearProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import rise from '@iconify/icons-ant-design/rise';
import fall from '@iconify/icons-ant-design/fall';

// ----------------------------------------------------------------------

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.grey[200]}`,
  background: theme.palette.background.paper,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2.5),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3),
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.customShadows.z8,
  }
}));

const MetricIcon = styled(Box)(({ theme, color }) => ({
  width: { xs: 40, sm: 48, md: 56 },
  height: { xs: 40, sm: 48, md: 56 },
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${color}20 0%, ${color}40 100%)`,
  border: `2px solid ${color}60`,
  marginBottom: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: theme.palette.grey[200],
  '& .MuiLinearProgress-bar': {
    borderRadius: 4,
  }
}));

// ----------------------------------------------------------------------

const MetricCard = ({ 
  title, 
  value, 
  change, 
  changeType = 'percentage',
  icon, 
  color = '#3182CE',
  isLoading = false,
  subtitle,
  progress,
  maxValue
}) => {
  const formatValue = (val) => {
    if (typeof val !== 'number') return val;
    if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
    return val.toLocaleString();
  };

  const formatChange = (change) => {
    if (changeType === 'percentage') {
      return `${change >= 0 ? '+' : ''}${change}%`;
    }
    return change;
  };

  const isPositive = change >= 0;
  const progressValue = progress !== undefined ? (progress / maxValue) * 100 : null;

  if (isLoading) {
    return (
      <StyledCard>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
        </Box>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary', 
              mb: 1,
              fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' }
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              color: 'text.primary', 
              mb: 1,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
            }}
          >
            {formatValue(value)}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {icon && (
          <MetricIcon color={color}>
            <Icon 
              icon={icon} 
              width={{ xs: 20, sm: 22, md: 24 }} 
              height={{ xs: 20, sm: 22, md: 24 }} 
              style={{ color: color }} 
            />
          </MetricIcon>
        )}
      </Box>

      {change !== undefined && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Icon 
            icon={isPositive ? rise : fall} 
            width={16} 
            height={16} 
            style={{ 
              color: isPositive ? '#38A169' : '#E53E3E',
              marginRight: 4
            }} 
          />
          <Typography 
            variant="body2" 
            sx={{ 
              color: isPositive ? 'success.main' : 'error.main',
              fontWeight: 600
            }}
          >
            {formatChange(change)}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
            vs previous period
          </Typography>
        </Box>
      )}

      {progressValue !== null && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Progress
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {Math.round(progressValue)}%
            </Typography>
          </Box>
          <ProgressBar 
            variant="determinate" 
            value={progressValue} 
            sx={{
              '& .MuiLinearProgress-bar': {
                background: `linear-gradient(90deg, ${color} 0%, ${color}80 100%)`,
              }
            }}
          />
        </Box>
      )}
    </StyledCard>
  );
};

// ----------------------------------------------------------------------

const DashboardMetricsGrid = ({ metrics = [], isLoading = false }) => {
  if (isLoading) {
    return (
      <Grid container spacing={3}>
        {[1, 2, 3, 4].map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item}>
            <StyledCard>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <CircularProgress />
              </Box>
            </StyledCard>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      {metrics.map((metric, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <MetricCard {...metric} />
        </Grid>
      ))}
    </Grid>
  );
};

export default DashboardMetricsGrid;
